import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  insertUserPreferences,
  EDIT_USER_STAFF_PREFERENCES,
  PROCESS_EDIT_USER_STAFF_PREFERENCES,
  EDIT_USER_STAFF_PREFERENCES_ERROR,
  ADD_NEW_USER_STAFF_PREFERENCES,
  PROCESS_NEW_USER_STAFF_PREFERENCES,
  USER_STAFF_PREFERENCES_CREATE_ERROR,
  PROCESS_EDIT_USER_CATEGORY_DISPLAY_ITEM,
  EDIT_USER_CATEGORY_DISPLAY_ITEMS,
  insertCategoryDisplayItems,
  appendCategoryDisplayItems,
  PROCESS_CATEGORY_DISPLAY_ITEMS,
  PROCESS_APPEND_CATEGORY_DISPLAY_ITEMS,
  GET_CATEGORY_DISPLAY_ITEMS,
  CATEGORY_DISPLAY_ITEMS_GET_ERROR,
  EDIT_USER_CATEGORY_DISPLAY_ITEMS_ERROR,
  insertUserCategoryDisplayItem,
  deleteUserCategoryDisplayItem,
  PROCESS_DELETE_USER_CATEGORY_DISPLAY_ITEM,
  GET_ALERT_GROUP_PERMISSIONS,
  PROCESS_ALERT_GROUP_PERMISSIONS,
  EDIT_ALERT_GROUP_PERMISSIONS,
  insertAlertGroupPermissions,
  addAlertGroupPermission,
  removeAlertGroupPermission,
  ADD_ALERT_GROUP_PERMISSION,
  PROCESS_ADDED_ALERT_GROUP_PERMISSIONS,
  PROCESS_REMOVED_ALERT_GROUP_PERMISSIONS,
  REMOVE_ALERT_GROUP_PERMISSION,
  deleteRemovedAlertGroupPermissions,
  insertAddedAlertGroupPermissions,
} from '../actions/userStaffPreferencesActions';
import { apiRequest } from '../actions/apiActions';
import { showLoading, hideLoading, addUiMsg } from '../actions/uiActions';
import { apiUrls, apiVersion } from '../../apiUrls';
import {
  wasUpdatedText,
  wasNotCreatedText,
  userText,
} from '../../components/shared/SharedInternationalizations';
import { messageTypes } from '../../components/ui/AlertMessage';
import { errorDisplay, redirect } from '../../utilities/general';
import { DEVICE_ERROR } from '../actions/deviceActions';
import { getUser } from '../actions/userActions';
import { loadAlertDeviceDetails } from '../actions/alertDeviceActions';

export const staffPreferencesText = (
  <FormattedMessage
    id="userStaffPreferencesMiddleware.preferencesText"
    defaultMessage="User Staff Preferences Profile For"
    description="The staff preferences for a user"
  />
);

/**
 * Middleware to add the newly created user to the Redux store
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUnit - Triggers adding the created user to the users object in Redux
 * @fires dispatch#reset - Resets the add new user form
 * @fires dispatch#userMsgAddSuccess - Triggers showing the user add success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const addNewUserStaffPreferences = (store) => (next) => (action) => {
  next(action);
  if (action.type === ADD_NEW_USER_STAFF_PREFERENCES) {
    const staffPreferences = {
      user: action.payload.user,
      refresh_rate: action.payload.refresh_rate,
      access_level: action.payload.access_level,
      display_mode: action.payload.display_mode,
      checkin_reports: action.payload.checkin_reports,
      show_panic_buttons: action.payload.show_panic_buttons,
      panic_title: action.payload.panic_title,
      disallow_web_alarm_confirm: action.payload.disallow_web_alarm_confirm,
      admin_reports: action.payload.admin_reports,
      edit_status_checkin_exclusion: action.payload.edit_status_checkin_exclusion,
    };

    const staffMeta = {
      method: 'PATCH',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].sarapreferences + action.payload.user + '/', // Add user ID to URL
      onSuccess: PROCESS_NEW_USER_STAFF_PREFERENCES,
      onError: USER_STAFF_PREFERENCES_CREATE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      formSteps: action.formSteps,
      previous: action.previous,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(staffPreferences, staffMeta));
  }
};

/**
 * Middleware to process the updated user preferences
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processNewStaffPreferences = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_NEW_USER_STAFF_PREFERENCES) {
    store.dispatch(hideLoading());
    store.dispatch(insertUserPreferences(action.payload));

    const userObj = store.getState().users.users[action.payload.user];
    let url = '/users/' + action.payload.user + '/view/';
    redirect(action, url);

    const messageText = (
      <React.Fragment>
        {staffPreferencesText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{userObj?.first_name || ''} {userObj?.last_name || ''}"
        </span>
        {wasUpdatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#userMsgEditError - Triggers showing the user edit error message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const newStaffPreferencesCreateError = (store) => (next) => (action) => {
  next(action);
  if (action.type === USER_STAFF_PREFERENCES_CREATE_ERROR) {
    store.dispatch(hideLoading());
    const userObj = store.getState().users.users[action.payload.user];

    const messageText = (
      <React.Fragment>
        {staffPreferencesText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{userObj?.first_name || ''} {userObj?.last_name || ''}"
        </span>
        {wasNotCreatedText}
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const editUserStaffPreferences = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_USER_STAFF_PREFERENCES) {
    const staffPreferences = {
      user: action.payload.user,
      refresh_rate: action.payload.refresh_rate,
      access_level: action.payload.access_level,
      display_mode: action.payload.display_mode,
      checkin_reports: action.payload.checkin_reports,
      show_panic_buttons: action.payload.show_panic_buttons,
      panic_title: action.payload.panic_title,
      disallow_web_alarm_confirm: action.payload.disallow_web_alarm_confirm,
      admin_reports: action.payload.admin_reports,
      edit_status_checkin_exclusion: action.payload.edit_status_checkin_exclusion,
    };

    const staffMeta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].sarapreferences +
        action.payload.user +
        '/',
      onSuccess: PROCESS_EDIT_USER_STAFF_PREFERENCES,
      onError: EDIT_USER_STAFF_PREFERENCES_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      formSteps: action.formSteps,
      previous: action.previous,
    };

    store.dispatch(apiRequest(staffPreferences, staffMeta));
  }
};

/**
 * Middleware to process the updated user preferences
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processEditUserStaffPreferences = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_EDIT_USER_STAFF_PREFERENCES) {
    store.dispatch(hideLoading());
    store.dispatch(insertUserPreferences(action.payload));

    let url = '/users/' + action.payload.user + '/view/';
    redirect(action, url);

    const userObj = store.getState().users.users[action.payload.user];
    const messageText = (
      <React.Fragment>
        {staffPreferencesText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{userObj?.first_name || ''} {userObj?.last_name || ''}"
        </span>
        {wasUpdatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to trigger an api call to retrieve all of the users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const getCategoryDisplayItems = (store) => (next) => (action) => {
  next(action);
  if (action.type === GET_CATEGORY_DISPLAY_ITEMS) {
    const body = null;
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].usercategoryorder,
      onSuccess: PROCESS_CATEGORY_DISPLAY_ITEMS,
      onError: CATEGORY_DISPLAY_ITEMS_GET_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processCategoryDisplayItems = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_CATEGORY_DISPLAY_ITEMS) {
    // Check if there are more pages to fetch
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_APPEND_CATEGORY_DISPLAY_ITEMS,
        onError: CATEGORY_DISPLAY_ITEMS_GET_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      // No more pages, hide loading
      store.dispatch(hideLoading());
    }
    
    // Process current page results
    const data = action.payload;
    const array = data.results;
    let categoriesObj = {};
    array.map((cat) => {
      categoriesObj[cat.id] = cat;
      return '';
    });
    store.dispatch(insertCategoryDisplayItems(categoriesObj));
  }
};

export const processAppendCategoryDisplayItems = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_APPEND_CATEGORY_DISPLAY_ITEMS) {
    // Check if there are more pages to fetch
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_APPEND_CATEGORY_DISPLAY_ITEMS,
        onError: CATEGORY_DISPLAY_ITEMS_GET_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      // No more pages, hide loading
      store.dispatch(hideLoading());
    }
    
    // Append current page results to existing data
    const data = action.payload;
    const array = data.results;
    let categoriesObj = {};
    array.map((cat) => {
      categoriesObj[cat.id] = cat;
      return '';
    });
    store.dispatch(appendCategoryDisplayItems(categoriesObj));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const categoryDisplayItemsLoadError = (store) => (next) => (action) => {
  next(action);
  if (action.type === CATEGORY_DISPLAY_ITEMS_GET_ERROR) {
    store.dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="userStaffPreferencesMiddleware.categoryDisplayItemsNotLoadedText"
          defaultMessage="Category Display Items list not loaded"
          description="The user statuses were not able to be loaded"
        />
      </React.Fragment>
    );

    const errorText = (
      <React.Fragment>
        <FormattedMessage
          id="userStaffPreferencesMiddleware.panicButtonsNotLoadedErrorText"
          defaultMessage="The category display items list was not loaded. If you are correctly logged in, try refreshing the page to reload"
          description="The units were not able to be loaded"
        />
      </React.Fragment>
    );

    const message = {
      message: messageText,
      error: errorText,
      messageType: messageTypes.danger,
    };

    store.dispatch(addUiMsg(message));
    console.error(action);
  }
};
/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const editCategoryDisplayItemsError = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_USER_CATEGORY_DISPLAY_ITEMS_ERROR) {
    store.dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="userStaffPreferencesMiddleware.categoryItemEditError"
          defaultMessage="There was an error editing the category display item"
          description="The user statuses were not able to be loaded"
        />
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };

    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const editUserCategoryDisplayItems = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_USER_CATEGORY_DISPLAY_ITEMS) {
    const addEdit = action.payload.addEdit;
    // add new items
    const added = addEdit.filter((item) => item.new === true);

    added.map((cat) => {
      const body = {
        username: action.payload.username,
        category_order: cat.category_order,
        alarm_device_type: cat.alarm_device_type,
      };
      const meta = {
        method: 'POST',
        url:
          apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].usercategoryorder,
        onSuccess: PROCESS_EDIT_USER_CATEGORY_DISPLAY_ITEM,
        onError: EDIT_USER_CATEGORY_DISPLAY_ITEMS_ERROR,
        auth: store.getState().auth.token,
        body: true,
        id: cat,
        user: action.payload.user,
        navigate: action.navigate,
        formSteps: action.formSteps,
        previous: action.previous,
      };
      store.dispatch(showLoading());
      store.dispatch(apiRequest(body, meta));
      return '';
    });
    // edit existing items
    const edited = addEdit.filter((item) => item.edit === true);

    edited.map((cat) => {
      const body = {
        username: action.payload.username,
        category_order: cat.category_order,
        alarm_device_type: cat.alarm_device_type,
      };
      const meta = {
        method: 'PATCH',
        url:
          apiUrls.baseUrl +
          apiVersion +
          apiUrls[apiVersion].usercategoryorder +
          cat.id +
          '/',
        onSuccess: PROCESS_EDIT_USER_CATEGORY_DISPLAY_ITEM,
        onError: EDIT_USER_CATEGORY_DISPLAY_ITEMS_ERROR,
        auth: store.getState().auth.token,
        body: true,
        id: cat,
        user: action.payload.user,
        navigate: action.navigate,
        formSteps: action.formSteps,
        previous: action.previous,
      };
      store.dispatch(showLoading());
      store.dispatch(apiRequest(body, meta));
      return '';
    });
    // delete removed items
    const removed = action.payload.removed;

    removed.map((cat) => {
      const body = null;
      const meta = {
        method: 'DELETE',
        url:
          apiUrls.baseUrl +
          apiVersion +
          apiUrls[apiVersion].usercategoryorder +
          cat +
          '/',
        onSuccess: PROCESS_DELETE_USER_CATEGORY_DISPLAY_ITEM,
        onError: EDIT_USER_CATEGORY_DISPLAY_ITEMS_ERROR,
        auth: store.getState().auth.token,
        body: false,
        id: cat,
        user: action.payload.user,
        navigate: action.navigate,
        formSteps: action.formSteps,
        previous: action.previous,
      };
      store.dispatch(showLoading());
      store.dispatch(apiRequest(body, meta));
      return '';
    });
  }
};

/**
 * Middleware to process the updated user preferences
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processUserCategoryDisplayItem = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_EDIT_USER_CATEGORY_DISPLAY_ITEM) {
    store.dispatch(hideLoading());
    store.dispatch(insertUserCategoryDisplayItem(action.payload));
    let url = '/users/' + action.payload.user + '/view/';
    redirect(action, url);

    const userObj = store.getState().users.users[action.meta.user];
    const messageText = (
      <React.Fragment>
        {staffPreferencesText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{userObj?.first_name || ''} {userObj?.last_name || ''}"
        </span>
        {wasUpdatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to process the updated user preferences
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processDeleteUserCategoryDisplayItem = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_DELETE_USER_CATEGORY_DISPLAY_ITEM) {
    store.dispatch(hideLoading());
    store.dispatch(deleteUserCategoryDisplayItem(action.meta.id));

    let url = '/users/' + action.payload.user + '/view/';
    redirect(action, url);

    const userObj = store.getState().users.users[action.meta.user];
    const messageText = (
      <React.Fragment>
        {staffPreferencesText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{userObj?.first_name || ''} {userObj?.last_name || ''}"
        </span>
        {wasUpdatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
  }
};

export const getAlertGroupPermissions = (store) => (next) => (action) => {
  next(action);
  if (action.type === GET_ALERT_GROUP_PERMISSIONS) {
    const body = null;
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].alertdeviceaccess,
      onSuccess: PROCESS_ALERT_GROUP_PERMISSIONS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

export const processAlertGroupPermissions = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_ALERT_GROUP_PERMISSIONS) {
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_ALERT_GROUP_PERMISSIONS,
        onError: DEVICE_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      store.dispatch(hideLoading());
    }
    const data = action.payload.results;
    const processedData = {};
    data.map((d) => {// eslint-disable-line
      processedData[d.id] = d;
    });
    store.dispatch(insertAlertGroupPermissions(processedData));
  }
};

/**
 * This middleware is used to process the device associations that have been
 * created/deleted in the alert and alarm device association forms
 */
export const editAlertGroupPermissions = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_ALERT_GROUP_PERMISSIONS) {
    const payload = action.payload;
    const navigate = action.navigate;
    const devices = payload.devices;
    const initialDevices = payload.initialDevices;
    devices.map((d) => {// eslint-disable-line
      if (!initialDevices.includes(d)) {
        const data = {};
        data.user = payload.user;
        data.alert_device = d;
        store.dispatch(addAlertGroupPermission(data, navigate));
      }
    });

    const groups = store.getState().users.alertGroupPermissions;
    const groupsArr = Object.values(groups);
    const filteredGroups = groupsArr.filter((a) => a.user === payload.user);
    initialDevices.map((d) => {// eslint-disable-line
      if (!devices.includes(d)) {
        const deviceAssociation = filteredGroups.filter((group) => {
          if (group.alert_device === d) {
            return true;
          } else {
            return false;
          }
        });

        store.dispatch(removeAlertGroupPermission(deviceAssociation, navigate));
      }
    });
  }
};
/**
 * Create a device association
 *
 * Submits the api call to create a device association and triggers the
 * show loading action. This is used for both alert devices and alarm
 * devices as well as for both users and units.
 */
export const addAlertGroupPermissionMdl = (store) => (next) => (action) => {
  next(action);
  if (action.type === ADD_ALERT_GROUP_PERMISSION) {
    const body = action.payload;
    const meta = {
      method: 'POST',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].alertdeviceaccess,
      onSuccess: PROCESS_ADDED_ALERT_GROUP_PERMISSIONS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Delete device association
 *
 * Sends a DELETE request to the api to delete a device association and triggers
 * the show loading action. This is used for both alert devices and alarm
 * devices as well as for both users and units.
 *
 * In the metadata being passed to the api call there is an item called "data"
 * that contains the body data that is also sent to the api. This object is
 * used in the process middleware to allow correct processing and updating
 * of data.
 */
export const removeAlertGroupPermissionMdl = (store) => (next) => (action) => {
  next(action);
  if (action.type === REMOVE_ALERT_GROUP_PERMISSION) {
    const body = action.payload[0];
    const meta = {
      method: 'DELETE',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alertdeviceaccess +
        body.id +
        '/',
      onSuccess: PROCESS_REMOVED_ALERT_GROUP_PERMISSIONS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
      data: body,
      navigate: action.navigate,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Process device association
 *
 * This processes the device association. Because the association api
 * call is just creating an association, it doesn't return any information
 * about the device or user/unit. So once the data is receieved, it is
 * then checked to see if the association is for an alert or alarm device
 * then it triggers an api call to get the details for that specific device.
 *
 * It cancels the loading for the initial call and then checks to see if the
 * association was for a user or a unit and triggers an api call for that
 * specific user/unit. By retrieving the full user/unit object it will then
 * pull all of the associated devices, which will include the newly associated
 * device and the device will display correctly in the interface.
 *
 * After triggering the call for the user/unit data, it then generates the url
 * for the user/unit that the association was for and triggers a redirect to
 * move the interface back to the display page for the user/unit. It also
 * creates an interface confirmation message that the association was successful.
 *
 * The reason the device details are fetched is because in the device
 * details, there is an array of ids for any users or units that the
 * devices is associated with. So to ensure that there is no confusion
 * in the interface regarding if the device is associated to anything,
 * the updated details are fetched which will ensure that the newly
 * removed association is reflected.
 */

export const processAddedAlertGroupPermissions = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_ADDED_ALERT_GROUP_PERMISSIONS) {
    const data = action.payload;
    store.dispatch(hideLoading());
    store.dispatch(insertAddedAlertGroupPermissions(data));

    let url = '/';
    if (data.user) {
      store.dispatch(getUser(data.user));
      url = '/users/' + data.user + '/view/?view=staff';
    }
    redirect(action, url);

    if (data.user) {
      const userObj = store.getState().users.users[data.user];
      const messageText = (
        <React.Fragment>
          {userText}:
          <span className="text-italic margin-left-5 margin-right-5">
            "{userObj?.first_name || ''} {userObj?.last_name || ''}"
          </span>
          {wasUpdatedText}
        </React.Fragment>
      );
      const message = {
        message: messageText,
        messageType: messageTypes.success,
      };

      store.dispatch(addUiMsg(message));
    }
  }
};

/**
 * Process a device association deletion
 *
 * When the association is deleted, no information is returned. So
 * the api middleware returns the metadata sent in the original api
 * call. This includes the device id and the user/unit id. Using this
 * data, the middleware checks to see if it's an alert or alarm device
 * and sends an api call to get the appropriate data.
 *
 * It then cancels the loading bar and checks to see if the association
 * was for a user or a unit. It triggers a call for the user/unit's
 * data and generates a url for that user/unit's display page and
 * triggers a redirect to that page. It then creates a confirmation
 * message that the association was removed.
 *
 * The reason the device details are fetched is because in the device
 * details, there is an array of ids for any users or units that the
 * devices is associated with. So to ensure that there is no confusion
 * in the interface regarding if the device is associated to anything,
 * the updated details are fetched which will ensure that the newly
 * removed association is reflected.
 */

export const processRemovedAlertGroupPermissions = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_REMOVED_ALERT_GROUP_PERMISSIONS) {
    const data = action.meta.data;
    if (data.alert_device) {
      store.dispatch(loadAlertDeviceDetails(data.alert_device));
    }
    store.dispatch(hideLoading());
    store.dispatch(deleteRemovedAlertGroupPermissions(data));

    let url = '/';
    if (data.user) {
      store.dispatch(getUser(data.user));
      url = '/users/' + data.user + '/view/?view=staff';
    }

    redirect(action, url);

    if (data.assigned_user) {
      const userObj = store.getState().users.users[data.assigned_user];
      const messageText = (
        <React.Fragment>
          {userText}:
          <span className="text-italic margin-left-5 margin-right-5">
            "{userObj?.first_name || ''} {userObj?.last_name || ''}"
          </span>
          {wasUpdatedText}
        </React.Fragment>
      );
      const message = {
        message: messageText,
        messageType: messageTypes.success,
      };

      store.dispatch(addUiMsg(message));
    }
  }
};

export const userStaffPreferencesMdl = [
  addNewUserStaffPreferences,
  processEditUserStaffPreferences,
  editUserStaffPreferences,
  newStaffPreferencesCreateError,
  processNewStaffPreferences,
  getCategoryDisplayItems,
  processDeleteUserCategoryDisplayItem,
  processUserCategoryDisplayItem,
  processCategoryDisplayItems,
  processAppendCategoryDisplayItems,
  editUserCategoryDisplayItems,
  categoryDisplayItemsLoadError,
  editCategoryDisplayItemsError,
  getAlertGroupPermissions,
  processAlertGroupPermissions,
  editAlertGroupPermissions,
  addAlertGroupPermissionMdl,
  removeAlertGroupPermissionMdl,
  processAddedAlertGroupPermissions,
  processRemovedAlertGroupPermissions,
];
